/**
 * Gestione robusta del ciclo di vita di navigator.share su iOS/PWA standalone.
 *
 * Problema: quando l'utente condivide verso un'app esterna (Instagram,
 * WhatsApp, ...) la PWA va in background e, su iOS, la promise di
 * navigator.share può non risolversi MAI (bug WebKit storico nelle PWA
 * standalone). Al ritorno in foreground l'app resterebbe con lo stato
 * "busy" bloccato e il canvas dell'anteprima svuotato dal sistema.
 *
 * Questo modulo è volutamente puro (nessun riferimento a React) per essere
 * unit-testabile: il componente collega gli eventi di visibilità e decide
 * COSA ripristinare, qui c'è solo il QUANDO.
 */

// Periodo di grazia dopo il ritorno in foreground: se iOS deve ancora
// consegnare l'esito dello share lo fa subito dopo la riattivazione;
// oltre questo tempo la promise è considerata persa.
export const SHARE_RECOVERY_GRACE_MS = 1200;

/**
 * Watchdog dello share "in volo".
 * - start() quando parte navigator.share → ritorna l'id dello share;
 * - settle(id) al resolve/reject (gli id vecchi vengono ignorati, così un
 *   esito tardivo non interferisce con uno share successivo);
 * - handleVisible() al ritorno in foreground: se dopo il periodo di grazia
 *   lo share è ancora in volo, onRecover() viene invocata.
 *
 * SEMANTICA (G3): "in volo al rientro in foreground" NON significa promise
 * persa — significa che la share è stata consegnata a un target esterno e
 * che su iOS il pannello nativo può essere ANCORA presentato (WebKit tiene
 * [[sharePromise]] non-null finché il controller non si conclude davvero;
 * dal web non è dismissabile). onRecover deve quindi portare l'utente in
 * uno stato coerente (chiudere lo sheet), non fingere che la share sia
 * finita. Un'eventuale navigator.share successiva può rigettare con
 * InvalidStateError "share() is already in progress": va gestita con
 * feedback esplicito, MAI silenziata.
 */
export function createShareRecovery({ onRecover, graceMs = SHARE_RECOVERY_GRACE_MS }) {
  let current = 0; // id dello share in volo (0 = nessuno)
  let seq = 0;
  let timer = null;

  const cancelTimer = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  return {
    start() {
      cancelTimer();
      current = ++seq;
      return current;
    },
    settle(id) {
      if (id !== current) return;
      current = 0;
      cancelTimer();
    },
    handleVisible() {
      if (!current) return;
      cancelTimer();
      const id = current;
      timer = setTimeout(() => {
        timer = null;
        if (id !== current) return;
        current = 0;
        onRecover();
      }, graceMs);
    },
    isInFlight() {
      return current !== 0;
    },
    dispose() {
      cancelTimer();
      current = 0;
    },
  };
}

/** Annullamento dello share sheet da parte dell'utente: non è un errore. */
export function isShareCancel(err) {
  if (!err) return false;
  return err.name === 'AbortError' || /abort|cancel/i.test(err.message || '');
}

/**
 * Uno share è già in corso (es. share sheet ancora aperto e l'utente tocca
 * di nuovo "Condividi" dopo un recovery): no-op silenzioso, non un errore.
 */
export function isShareAlreadyInProgress(err) {
  if (!err) return false;
  return err.name === 'InvalidStateError' || /in progress|not yet completed/i.test(err.message || '');
}

/**
 * Workaround documentato per il freeze di rendering delle PWA iOS al rientro
 * dal background: il DOM è vivo ma il compositor non aggiorna più il frame.
 * Un reflow forzato (display none + lettura layout + ripristino) obbliga
 * WebKit a ricomporre l'intera pagina. È sincrono nello stesso frame:
 * nessun flash visibile; su pagine sane costa un solo reflow.
 *
 * LIMITE (G2): il diff di stile è a somma zero (lo stile computato finale è
 * identico a quello iniziale), quindi al termine del task WebKit non ha
 * alcuna differenza visiva da committare: forza il LAYOUT ma non garantisce
 * un nuovo frame composto. Al resume da background serve anche un commit
 * reale: vedi scheduleResumeCommit.
 */
export function forceRepaint(el) {
  const target = el || (typeof document !== 'undefined' ? document.body : null);
  if (!target) return;
  const prev = target.style.display;
  target.style.display = 'none';
  void target.offsetHeight;
  target.style.display = prev;
}

/**
 * Commit di compositing garantito al resume (G2).
 *
 * Al rientro da un'app esterna iOS mostra lo snapshot di sistema (che può
 * includere il "fantasma" del native share sheet) finché WebKit non
 * committa un frame NUOVO. Se dal punto di vista di WebKit nulla è
 * cambiato, il commit può non arrivare finché l'utente non tocca lo
 * schermo (un input forza un rendering update).
 *
 * Strategia a fasi:
 * 1. subito: diff compositor-only REALE (opacity 0.999) sull'elemento →
 *    c'è qualcosa da committare; la richiesta stessa di rAF schedula un
 *    rendering update in WebKit;
 * 2. al PRIMO frame davvero eseguito dopo il foreground: secondo ridisegno
 *    del contenuto (redraw) — copre il purge del canvas avvenuto dopo il
 *    visibilitychange;
 * 3. al frame successivo: ripristino dell'opacity.
 * 0.999 è visivamente impercettibile: nessun flash.
 */
export function scheduleResumeCommit(el, redraw, raf) {
  const request = raf || ((cb) => requestAnimationFrame(cb));
  if (el) el.style.opacity = '0.999';
  request(() => {
    if (typeof redraw === 'function') redraw();
    request(() => {
      if (el) el.style.opacity = '';
    });
  });
}
